import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { Subscription } from 'rxjs';
import { GeneralService, Address } from '@gs-2.0/core';

const CHIP_TO_FIELD: Record<string, keyof Address> = {
  // Underscore format (stored by API / settings endpoint)
  'STREET_NAME':         'street',
  'STREET_NUMBER':       'streetNumber',
  'CITY':                'city',
  'STATE_PROVINCE':      'state',
  'POSTAL_CODE':         'postalCode',
  'COUNTRY':             'country',
  'LANDMARK_ADDRESS':    'landmarkAddress',
  // Space/slash format (frontend defaults / legacy saved values)
  'STREET NAME':         'street',
  'STREET/HOUSE NUMBER': 'streetNumber',
  'STATE/PROVINCE':      'state',
  'ZIP/POSTAL CODE':     'postalCode',
  'LANDMARK ADDRESS':    'landmarkAddress',
};

const DEFAULT_FORMAT: string[] = [
  'STREET_NAME', 'CITY', 'STATE_PROVINCE', 'POSTAL_CODE', 'COUNTRY'
];

@Pipe({
  name: 'addressFormat',
  standalone: true,
  pure: false,
})
export class AddressFormatPipe implements PipeTransform, OnDestroy {
  private subscription: Subscription;

  constructor(
    private generalService: GeneralService,
    private cdr: ChangeDetectorRef,
  ) {
    this.subscription = this.generalService.settings$.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  transform(address: Address | null | undefined, formatOverride?: string[]): string {
    if (!address) return '';

    const format = formatOverride ?? this.getAddressFormat();
    const seen = new Set<string>();
    const parts: string[] = [];

    for (const chip of format) {
      const field = CHIP_TO_FIELD[chip];
      if (!field || seen.has(field)) continue;
      seen.add(field);
      const value = address[field];
      if (value) parts.push(value);
    }

    return parts.join(', ');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getAddressFormat(): string[] {
    const settings = this.generalService.settings;
    if (!settings) return DEFAULT_FORMAT;

    const configItem = settings.response.globalConfig.find(c => c.key === 'addressFormat');
    if (!configItem?.value) return DEFAULT_FORMAT;

    try {
      const parsed = JSON.parse(configItem.value);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_FORMAT;
    } catch {
      return DEFAULT_FORMAT;
    }
  }
}
